/* eslint-disable no-extra-boolean-cast */
/* global $ */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { decodePassengers } from 'utils/Reserbus';
import {
  productListTracker,
  resultsActionsTracker,
  sortTracker,
  filterTracker,
} from 'metrics/user-analytics/search';
import { selectedTripTracker } from 'metrics/user-analytics/trip';
import { Visibility, Spacing, Text } from '@reservamos/elements';
import LoadingScreen from 'components/LoadingScreen';
import TripListHeader from 'components/TripListHeader';
import TripList from 'components/search/TripList';
import DiscountUITracker from 'components/search/DiscountUITracker';
import PromoUrgencyBannerContainer from 'components/search/PromoUrgencyBannerContainer';
import { getMomentFromSearchDateParam } from 'utils/search/searchDateParam';
import normalizeSearchWidgetDateFormat from 'utils/normalizeSearchWidgetDateFormat';
import DiscountBanner from 'ui/atoms/DiscountBanner';
import { When } from 'react-if';
import LineBanner from 'ui/atoms/LineBanner';
import { trackRecommendedTripSelected } from 'utils/userPreferences';
import RouteTerminalsDisplay from 'ui/molecules/RouteTerminalsDisplay';
import { getSeatsPurchasePayload, isSeatsOnResultActivated } from 'utils/seats';
import PoweredBy from 'components/PoweredBy';
import DaysCarousel from 'components/search/DaysCarousel';
import NoResults from '../../../ui/molecules/NoResults';
import SearchSidebar from '../../SearchSidebar';
import LabelLoading from '../../../ui/molecules/LabelLoading';
import NormalTicketButton from '../NormalTicketButton';
import NormalTicketWrapper from '../NormalTicketWrapper';
import Coupon from '../SearchCoupon/SearchCoupon';
import ChangeProvider from '../../../ui/atoms/ChangeProvider';
import OpenTicketList from '../OpenTicketList';
import DayControls from '../DayControls';
import NerbyTerminals from '../../../ui/atoms/NerbyTerminals';
import ResultsTitle from '../../../ui/atoms/ResultsTitle';
import DepartureTripPicked from '../../../ui/molecules/DepartureTripPicked';
import { cleanAnalyticsParams } from '../../../utils/urls';
import RecommendedTripsList from '../RecommendedTrips';
import SwitchDoters from '../SwitchDoters/SwitchDoters';
import OpenTicketCTA from '../OpenTicketCTA';
import ClosedTripsSection from '../ClosedTripsSection';
import BenefitDenialModal from '../BenefitDenialModal/BenefitDenialModal';

const GENERAL_PASSENGER_TYPES = ['adult', 'general', 'child_in_arms'];

/**
 * Maps a full-gratuity benefit category to its half-fare (50%) equivalent. Used to detect when the
 * 100% free slot (e.g. idoso) is sold out but the paid 50% fare is still available, so the
 * benefit-denial modal can offer buying at 50% as an alternative to issuing the denial.
 */
const HALF_FARE_BY_CATEGORY = {
  older: 'older_half',
  disadvantaged_youth: 'disadvantaged_youth_half',
};

/**
 * Get the first selected non-general passenger category.
 * @param {string} encodedPassengers - Encoded passengers string from URL.
 * @returns {string|undefined} The first non-general passenger type with count > 0, or undefined.
 */
const getSelectedPassengerCategory = (encodedPassengers) => {
  const passengers = decodePassengers(encodedPassengers);

  return Object.keys(passengers).find(
    (passengerType) =>
      !GENERAL_PASSENGER_TYPES.includes(passengerType) && passengers[passengerType] > 0,
  );
};

const propTypes = {
  origin: PropTypes.object.isRequired,
  destination: PropTypes.object.isRequired,
  departureId: PropTypes.string.isRequired,
  passengers: PropTypes.string.isRequired,
  departureDate: PropTypes.string.isRequired,
  returnDate: PropTypes.string,
  providers: PropTypes.array,
  provider: PropTypes.object,
  transportType: PropTypes.string,
  searchIsPolling: PropTypes.bool.isRequired,
  paymentPlans: PropTypes.objectOf(PropTypes.number).isRequired,
  installmentsMinAmount: PropTypes.number.isRequired,
  trips: PropTypes.array.isRequired,
  promoUrgencyTrips: PropTypes.array.isRequired,
  closedTrips: PropTypes.array,
  noTrips: PropTypes.bool.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortProvidersBy: PropTypes.string,
  departureFilter: PropTypes.string.isRequired,
  locations: PropTypes.object.isRequired,
  departureLocationFilter: PropTypes.string.isRequired,
  arrivalLocationFilter: PropTypes.string.isRequired,
  activeDepartureFilter: PropTypes.bool.isRequired,
  activeLocationFilter: PropTypes.bool.isRequired,
  activeStopsFilter: PropTypes.bool.isRequired,
  tooManyFilters: PropTypes.bool.isRequired,
  currentPath: PropTypes.string.isRequired,
  seenPrice: PropTypes.string,
  showModal: PropTypes.func.isRequired,
  setSortOrder: PropTypes.func.isRequired,
  setTripFilter: PropTypes.func.isRequired,
  resetTripFilters: PropTypes.func.isRequired,
  stopsFilter: PropTypes.func.isRequired,
  transitionTo: PropTypes.func.isRequired,
  replaceUrl: PropTypes.func.isRequired,
  hasMultipleProviders: PropTypes.bool.isRequired,
  openTrip: PropTypes.object,
  isStopsFilterAvailable: PropTypes.object,
  categoriesFilter: PropTypes.array,
  tripOptionsFilter: PropTypes.array,
  t: PropTypes.func.isRequired,
  discountedTripsByCoupon: PropTypes.array,
  isOpenTicketList: PropTypes.bool,
  setRedirectParams: PropTypes.func,
  isLogged: PropTypes.bool,
  providerId: PropTypes.string,
  areOpenTicketTrips: PropTypes.bool,
  features: PropTypes.object.isRequired,
  returnIsOpenTicket: PropTypes.bool,
  tripDate: PropTypes.string,
  tripDepartureDuration: PropTypes.string,
  tripTime: PropTypes.string,
  originCity: PropTypes.string,
  destinationCity: PropTypes.string,
  redirectToDeparture: PropTypes.func,
  departureTrip: PropTypes.object,
  setTripFilters: PropTypes.func.isRequired,
  tripsWithoutFilters: PropTypes.array,
  filters: PropTypes.object,
  searchOriginId: PropTypes.string,
  searchDestinationId: PropTypes.string,
  discountPercent: PropTypes.number,
  resultPriceToShow: PropTypes.bool,
  firstTripLineId: PropTypes.string,
  firstTripLineLogo: PropTypes.string,
  showSpecialDiscountBanner: PropTypes.bool,
  specialDiscountVariant: PropTypes.oneOf(['etn', 'gho', 'costa', 'gfa', 'rochoa']),
  brand: PropTypes.string,
  dateFormat: PropTypes.string,
  tripTerminalInfo: PropTypes.object,
  departureQueryTripInfo: PropTypes.object,
  passengersNotFound: PropTypes.array,
  couponCode: PropTypes.string,
  passengersQueryParam: PropTypes.string,
  hasValidDepartureSeats: PropTypes.array,
  newPurchase: PropTypes.func,
  location: PropTypes.object,
  roundTrip: PropTypes.bool.isRequired,
  departureIsOpenTicket: PropTypes.bool,
  updateDepartureIsOpen: PropTypes.func,
  analyticsSearchData: PropTypes.object,
};

/**
 * Represents the ProviderReturns component.
 */
class ProviderReturns extends Component {
  /**
   * Constructs a new ProviderReturns component.
   * @param {object} props - The component props.
   */
  constructor(props) {
    super(props);

    this.optionsClick = this.optionsClick.bind(this);
    this.setSortOrder = this.setSortOrder.bind(this);
    this.setActiveFilter = this.setActiveFilter.bind(this);
    this.setActiveFilters = this.setActiveFilters.bind(this);
    this.selectTrip = this.selectTrip.bind(this);
    this.selectTripDefault = this.selectTripDefault.bind(this);
    this.selectTripRedirect = this.selectTripRedirect.bind(this);
    this.goToHome = this.goToHome.bind(this);
    this.goToReturnProviders = this.goToReturnProviders.bind(this);
    this.selectOpenTripList = this.selectOpenTripList.bind(this);
    this.useOpenTicketIfAvailable = this.useOpenTicketIfAvailable.bind(this);
    this.handleOnRedirectToDeparture = this.handleOnRedirectToDeparture.bind(this);
    this.departureSelectedTripChecked = false;
    this.state = { introDone: false };
  }

  componentDidMount() {
    const { trips, departureIsOpenTicket, updateDepartureIsOpen } = this.props;

    if (departureIsOpenTicket === true || departureIsOpenTicket === 'true') {
      updateDepartureIsOpen(true);
    }

    this.validateDepartureSeats();

    $(window).scrollTop(0);
    /*
      FIXME correct the scroll issue with the address bar
    */
    setTimeout(() => $(window).scrollTop(0), 300);

    if (trips.length) {
      this.trackProductList();
    }
    cleanAnalyticsParams();
    this.useOpenTicketIfAvailable();
    this.introT = setTimeout(() => this.setState({ introDone: true }), 1500);
  }

  componentDidUpdate(prevProps) {
    const { trips, provider } = this.props;

    this.validateDepartureSeats();

    if (provider && prevProps.trips.length !== trips.length) {
      this.trackProductList();
    }

    if (prevProps.trips.length !== trips.length) {
      this.useOpenTicketIfAvailable();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.introT);
    const { resetTripFilters, features } = this.props;

    if (!features.NEW_FILTERS_VERSION) resetTripFilters();
  }

  handleOnRedirectToDeparture() {
    const { features, redirectToDeparture } = this.props;
    redirectToDeparture(features.PROVIDERS_SELECTION_ENABLED);
  }

  getAnalyticsData() {
    const { origin, destination, departureDate, returnDate } = this.props;

    return {
      origin,
      destination,
      roundTrip: true,
      departureDate: moment(departureDate, 'DD-MMM-YY').toDate(),
      returnDate: moment(returnDate, 'DD-MMM-YY').toDate(),
    };
  }

  setSortOrder(sortBy, list) {
    const { setSortOrder } = this.props;
    setSortOrder(sortBy, list);
    sortTracker(this.getAnalyticsData(), sortBy, 'Returns');
  }

  setActiveFilter(filterBy, active) {
    const { setTripFilter } = this.props;
    setTripFilter(filterBy, active);
    filterTracker(this.getAnalyticsData(), active, 'Returns');
  }

  /**
   * Applied several filters to the search
   * @param {Object} filters - Filters applied
   * @param {*} isFiltersUpdating - Indicates if it is an update of filters but not made by the user
   */
  setActiveFilters(filters, isFiltersUpdating) {
    const { setTripFilters } = this.props;
    setTripFilters(filters);
    if (!isFiltersUpdating) filterTracker(this.getAnalyticsData(), filters, 'Departures');
  }

  /**
   * Validates if the seats selected for the departure trip are valid,
   * this is check if the seats are selected in results page
   */
  validateDepartureSeats() {
    const { redirectToDeparture, hasValidDepartureSeats, departureTrip, features } = this.props;
    const isSeatsOnResults = isSeatsOnResultActivated() && !departureTrip?.hasConnections;

    const isRedirectedTrip = departureTrip?.redirectTo && features.TRIP_REDIRECTION_LEAD;
    if (
      !this.departureSelectedTripChecked &&
      departureTrip?.id &&
      departureTrip?.allowsSeatSelection &&
      !isRedirectedTrip &&
      isSeatsOnResults &&
      !hasValidDepartureSeats
    ) {
      this.departureSelectedTripChecked = true;
      redirectToDeparture();
    }
  }

  useOpenTicketIfAvailable() {
    const { openTrip, returnIsOpenTicket } = this.props;
    // Only retrieve open ticket if flag is true
    if (!returnIsOpenTicket) return;

    if (openTrip) {
      this.selectTrip(openTrip);
    }
  }

  trackProductList() {
    const { transportType, trips, provider, passengers } = this.props;

    // Check if provider is undefined
    if (provider === undefined) {
      console.error('Provider is undefined');
      return; // Optionally, return or handle the case where provider is undefined
    }

    const { name } = provider; // This is now safe because you've checked that provider is not undefined

    productListTracker(transportType, trips, name, decodePassengers(passengers), 'return');
  }

  optionsClick(option, modalComponent) {
    const { showModal } = this.props;

    showModal(modalComponent);
    resultsActionsTracker(this.getAnalyticsData(), option, 'Returns');
  }

  selectTripDefault(trip, position) {
    const { departureId, passengers, transitionTo, replaceUrl, seenPrice, returnIsOpenTicket } =
      this.props;
    const route = `/purchase/${departureId}/${trip.id}/new/${passengers}`;
    const roundSeenPrice = Number(seenPrice) + Number(trip.pricing.total);

    selectedTripTracker(trip, position, 'return');
    if (returnIsOpenTicket) {
      replaceUrl(route, { seenPrice: roundSeenPrice });
    } else {
      transitionTo(route, {
        seenPrice: roundSeenPrice,
      });
    }
  }

  selectTripRedirect(trip) {
    const {
      roundTrip,
      setRedirectParams,
      passengersQueryParam,
      newPurchase,
      location,
      departureTrip,
    } = this.props;
    const { redirectTo, departure, originId, destinationId, lineId, id } = trip;
    const { brand } = redirectTo;

    const departureDate = moment(departure);

    const selectedSeats =
      isSeatsOnResultActivated() &&
      getSeatsPurchasePayload({
        departureId: departureTrip.id,
        returnId: id,
      });
    // Used to fetch the slug of the depart trip
    const searchParams = new URLSearchParams(location.search);
    const departsSlug = searchParams.get('departs');

    const params = {
      return_origin: originId,
      return_destination: destinationId,
      return_date: departureDate.format('DD-MM-YYYY'),
      return_hour: departureDate.format('HH'),
      return_minutes: departureDate.format('mm'),
      return_line: lineId,
      passengers: passengersQueryParam,
      selected_seats: selectedSeats && JSON.stringify(selectedSeats),
    };

    const shouldRedirectNow = true;
    const waitForPurchase = true;

    newPurchase(departsSlug, trip.id, trip.pricing.total);

    setRedirectParams('return', params, brand, shouldRedirectNow, roundTrip, waitForPurchase);
  }

  selectTrip(trip, position) {
    const { features } = this.props;
    const { redirectTo, recommendationType } = trip;
    if (recommendationType) {
      trackRecommendedTripSelected({ way: 'return', recommendationType });
    }

    if (features.TRIP_REDIRECTION_LEAD && redirectTo) {
      return this.selectTripRedirect(trip);
    }

    return this.selectTripDefault(trip, position);
  }

  selectOpenTripList() {
    const {
      passengers,
      transitionTo,
      departureDate,
      departureId,
      returnDate,
      searchOriginId,
      searchDestinationId,
    } = this.props;
    const route =
      `/search/${searchOriginId}/${searchDestinationId}/${departureDate}/${returnDate}` +
      `/p/${passengers}/${departureId}/returns/open`;

    transitionTo(route);
  }

  goToHome() {
    const { transitionTo } = this.props;
    transitionTo('/');
  }

  goToReturnProviders() {
    const { currentPath, replaceUrl, features } = this.props;
    const route = currentPath.split('/');

    // Remove current provider id from currentPath array and add 'provider' to go
    // to return providers route
    route.pop();
    route.pop();

    if (features.PROVIDERS_SELECTION_ENABLED) {
      route.push('return', 'providers');
    } else {
      route.push('returns');
    }

    replaceUrl(route.join('/'));
  }

  hasFiltersApplied() {
    const { filters } = this.props;
    return (
      !filters.departureTime.includes('none') ||
      filters.categories.length ||
      !filters.stops.includes('none')
    );
  }

  renderTripList(openTicket = null) {
    const {
      isOpenTicketList,
      isLogged,
      providerId,
      departureTrip,
      filters,
      trips,
      promoUrgencyTrips,
      tripsWithoutFilters,
      features,
      passengersNotFound,
      couponCode,
      discountedTripsByCoupon,
      origin,
      destination,
      closedTrips,
      roundTrip,
      showSpecialDiscountBanner,
      specialDiscountVariant,
      analyticsSearchData,
      searchIsPolling,
      returnDate,
    } = this.props;

    const showOpenTicket =
      !this.hasFiltersApplied() || (this.hasFiltersApplied() && !tripsWithoutFilters.length);

    return isOpenTicketList ? (
      <OpenTicketList way="return" selectTrip={this.selectTrip} roundTrip providerId={providerId} />
    ) : (
      <Spacing responsiveSize="S" vertical>
        <DiscountUITracker
          trips={promoUrgencyTrips}
          roundTrip={roundTrip}
          way="return"
          analyticsSearchData={analyticsSearchData}
          searchIsPolling={searchIsPolling}
          searchDate={returnDate}
          departureTrip={departureTrip}
          scopeId={providerId}
        />
        <PromoUrgencyBannerContainer
          trips={promoUrgencyTrips}
          roundTrip={roundTrip}
          way="return"
          analyticsSearchData={analyticsSearchData}
          searchIsPolling={searchIsPolling}
          departureDate={returnDate}
          departureTrip={departureTrip}
        />
        <RecommendedTripsList
          couponCode={couponCode}
          discountedTripsByCoupon={discountedTripsByCoupon}
          way="return"
          selectTrip={this.selectTrip}
          onOptionClick={this.optionsClick}
          providerId={providerId}
          filtersApplied={filters}
          departureTrip={departureTrip}
          searchTrips={trips}
          isRoundTrip={roundTrip}
          isExchange={false}
          originCityName={origin?.cityName}
          destinationCityName={destination?.cityName}
          showSpecialDiscountBanner={showSpecialDiscountBanner}
          specialDiscountVariant={specialDiscountVariant}
        />

        <TripList
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.props}
          way="return"
          selectTrip={this.selectTrip}
          onOptionClick={this.optionsClick}
          tripOpenTicket={showOpenTicket && openTicket}
          isLogged={isLogged}
          filtersApplied={filters}
          showPoweredBy={!tripsWithoutFilters.length && (!closedTrips || closedTrips.length === 0)}
          departureTrip={departureTrip}
          showDotersBanner={!tripsWithoutFilters.length && !isLogged && features.DOTERS_ENABLED}
          passengersNotFound={passengersNotFound}
        />
      </Spacing>
    );
  }

  renderTripListWithoutFilters(openTicket = null) {
    const { tripsWithoutFilters, isLogged, filters, isOpenTicketList, features, departureTrip } =
      this.props;
    if (isOpenTicketList) return null;
    return (
      <TripList
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.props}
        trips={tripsWithoutFilters}
        way="return"
        selectTrip={this.selectTrip}
        onOptionClick={this.optionsClick}
        tripOpenTicket={openTicket}
        isLogged={isLogged}
        filtersApplied={filters}
        isNotFiltered
        showDotersBanner={tripsWithoutFilters.length && !isLogged && features.DOTERS_ENABLED}
        departureTrip={departureTrip}
      />
    );
  }

  render() {
    const {
      provider,
      providers,
      sortBy,
      sortProvidersBy,
      departureFilter,
      departureLocationFilter,
      arrivalLocationFilter,
      activeStopsFilter,
      noTrips,
      locations,
      searchIsPolling,
      showModal,
      activeLocationFilter,
      activeDepartureFilter,
      tooManyFilters,
      openTrip,
      stopsFilter,
      categoriesFilter,
      tripOptionsFilter,
      trips,
      isStopsFilterAvailable,
      isOpenTicketList,
      t,
      providerId,
      areOpenTicketTrips,
      features,
      returnDate,
      returnIsOpenTicket,
      tripDate,
      tripDepartureDuration,
      tripTime,
      originCity,
      destinationCity,
      departureTrip,
      resetTripFilters,
      tripsWithoutFilters,
      discountPercent,
      resultPriceToShow,
      firstTripLineId,
      firstTripLineLogo,
      brand,
      dateFormat,
      tripTerminalInfo,
      closedTrips,
      roundTrip,
      isLogged,
      filters,
      origin,
      destination,
      passengers,
    } = this.props;
    const hasAvailableTrips = trips.length > 0;
    const hasResults = isOpenTicketList || !noTrips || (noTrips && tooManyFilters);
    const selectedCategory =
      getSelectedPassengerCategory(passengers) ||
      categoriesFilter?.[0] ||
      categoriesFilter?.get?.(0);
    const isBenefitCategory =
      features.FREE_FARE_BENEFIT_DENIAL &&
      selectedCategory &&
      !GENERAL_PASSENGER_TYPES.includes(selectedCategory);
    /**
     * Check whether the trip has availability for the selected benefit category.
     * @param {object} trip - Trip from search results.
     * @returns {boolean} True when the category has seats available.
     */
    const tripHasCategoryAvailability = (trip) =>
      trip.passengerTypes?.some((pt) => pt.type === selectedCategory && pt.availability > 0);
    const categoryHasAvailability =
      searchIsPolling ||
      !isBenefitCategory ||
      (!noTrips && trips.some(tripHasCategoryAvailability));
    // When the full gratuity (e.g. idoso 100% = older) is sold out, check if the half-fare
    // equivalent (idoso 50% = older_half) is available. The booking already falls back to it
    // (see getPassengerCategory), so the denial modal can offer buying at 50% or issuing the denial.
    const halfFareCategory = HALF_FARE_BY_CATEGORY[selectedCategory];
    const tripHasHalfFareAvailability = (trip) =>
      trip.passengerTypes?.some((pt) => pt.type === halfFareCategory && pt.availability > 0);
    const halfFareAvailable =
      isBenefitCategory &&
      !categoryHasAvailability &&
      Boolean(halfFareCategory) &&
      !noTrips &&
      trips.some(tripHasHalfFareAvailability);

    const returnMoment = getMomentFromSearchDateParam(returnDate);
    const returnDateFormat = normalizeSearchWidgetDateFormat(dateFormat, 'DD-MMMM-YY');
    const fullMonth = returnMoment.format('MMMM');
    const returnDateText = returnMoment
      .format(returnDateFormat)
      .replace(fullMonth, fullMonth.slice(0, 3));

    const searchingText = t('general:searching');
    const { introDone } = this.state;
    const pollingBlock =
      searchIsPolling &&
      (noTrips ||
        (returnIsOpenTicket && !isOpenTicketList) ||
        features.HIDE_CACHED_TRIPS_DURING_POLLING);

    let openTicketCTA = (
      <OpenTicketCTA
        openTrip={openTrip}
        way="return"
        isRoundTrip
        showWalletPoints={resultPriceToShow.priceType === 'wallet'}
        walletType={resultPriceToShow.walletType}
        onSelect={
          features.OPEN_TICKET_PROVIDER_SELECTION_ENABLED
            ? this.selectOpenTripList
            : this.selectTrip
        }
      />
    );
    if (isOpenTicketList && openTrip) {
      openTicketCTA = (
        <NormalTicketWrapper>
          <NormalTicketButton way="return" onClick={this.goToReturnProviders} />
        </NormalTicketWrapper>
      );
    }

    const TerminalsComponent = <NerbyTerminals onColumns />;
    const showDaysOpenList = !isOpenTicketList || areOpenTicketTrips;

    const scheduleTitle = isOpenTicketList ? 'openReturn' : 'schedulesReturn';
    const activeFiltersFunction = features.NEW_FILTERS_VERSION
      ? this.setActiveFilters
      : this.setActiveFilter;

    const lineOrDiscountBannerAreShown =
      features.SHOW_DISCOUNT_BANNER || features.EXTRA_RESULT_LINES?.includes(firstTripLineId);

    const isNewResultsDesign = features.NEW_RESULTS_DESIGN;
    const isSeatsOnResults = isSeatsOnResultActivated();

    let useSearchSidebar = features.SEARCH_SIDEBAR_ENABLED;
    if ((brand === 'berlinas' && isNewResultsDesign) || isSeatsOnResults) useSearchSidebar = false;

    const showLoadingScreen = !introDone || pollingBlock;

    const containerClassName = `returns-container ${
      !isSeatsOnResults && (useSearchSidebar || isNewResultsDesign) ? 'with-sidebar' : ''
    }`;

    return (
      <>
        {!categoryHasAvailability && (
          <BenefitDenialModal
            origin={origin}
            destination={destination}
            date={returnDate}
            category={selectedCategory}
            halfFareAvailable={halfFareAvailable}
          />
        )}
        <DaysCarousel way="return" containerClassName={containerClassName} />
        {showLoadingScreen ? (
          <LoadingScreen loadingText={searchingText} hasHeader={false} isReturn={!introDone} />
        ) : null}
        {hasResults ? (
          <>
            <div className={containerClassName}>
              <Spacing vertical size="M">
                <DepartureTripPicked
                  departureDate={tripDate}
                  departureDuration={tripDepartureDuration}
                  departureTime={tripTime}
                  originCity={originCity}
                  destinationCity={destinationCity}
                  onClick={this.handleOnRedirectToDeparture}
                  departureLineLogo={departureTrip.line?.logoUrl}
                  isOpenTicket={departureTrip.openTicket}
                />
              </Spacing>
              {!useSearchSidebar && (
                <>
                  <TripListHeader
                    way="return"
                    provider={provider}
                    hasOtherProviders={providers.length > 1}
                    sortOn={!!sortBy}
                    departureFilterOn={activeDepartureFilter}
                    locationFilterOn={activeLocationFilter}
                    stopsFilterOn={activeStopsFilter}
                    onOptionClick={this.optionsClick}
                    showLocationFilter={
                      locations.departure.length > 2 || locations.arrival.length > 2
                    }
                    locations={locations}
                    floatingOptions={
                      2 + (locations.departure.length > 2 || locations.arrival.length > 2)
                    }
                    sortProperty={sortBy}
                    providerSortProperty={sortProvidersBy}
                    activeDeparture={departureFilter}
                    departureLocation={departureLocationFilter}
                    arrivalLocation={arrivalLocationFilter}
                    onSortSelect={this.setSortOrder}
                    onFilterSelect={activeFiltersFunction}
                    showModal={showModal}
                    onChangeProviderClick={this.goToReturnProviders}
                    stopsFilter={stopsFilter}
                    trips={trips}
                    date={returnDateText}
                    isStopsFilterAvailable={isStopsFilterAvailable}
                    isOpenTicketList={isOpenTicketList}
                    onResetFilters={resetTripFilters}
                    isPolling={searchIsPolling}
                  />

                  {isNewResultsDesign && (
                    <Spacing
                      justifyContent={lineOrDiscountBannerAreShown ? 'space-between' : 'flex-end'}
                      size="S"
                    >
                      <When condition={features.EXTRA_RESULT_LINES?.includes(firstTripLineId)}>
                        <LineBanner brand={firstTripLineId} logoUrl={firstTripLineLogo} />
                      </When>
                      <When
                        condition={
                          features.SHOW_DISCOUNT_BANNER &&
                          !features.EXTRA_RESULT_LINES?.includes(firstTripLineId)
                        }
                      >
                        <DiscountBanner discountPercent={discountPercent} way="return" />
                      </When>
                    </Spacing>
                  )}
                </>
              )}
              {useSearchSidebar && (
                <>
                  <div className="sidebar-title">
                    {features.USE_HORIZONTAL_RESULTS ? (
                      <p className="sidebar-title-copy">{t('trips:label.discounts_available')}</p>
                    ) : (
                      <p className="sidebar-title-copy">{t('search:label.sort_by')}</p>
                    )}
                  </div>
                  <SearchSidebar
                    onChangeSort={this.setSortOrder}
                    onChangeScheduleFilter={activeFiltersFunction}
                    scheduleFilter={departureFilter}
                    categoriesFilter={categoriesFilter}
                    tripOptionsFilter={tripOptionsFilter}
                    isStopsFilterAvailable={isStopsFilterAvailable}
                    stopsFilter={stopsFilter}
                    sort={sortBy}
                    way="return"
                    hasOtherProviders={providers.length > 1}
                    onChangeProviderClick={this.goToReturnProviders}
                    onResetFilters={resetTripFilters}
                    isOpenTicketList={isOpenTicketList}
                    isPolling={searchIsPolling}
                  />
                  <ResultsTitle
                    sectionTitle={scheduleTitle}
                    date={!isOpenTicketList && returnDateText}
                    rightContent={isNewResultsDesign ? <SwitchDoters /> : openTicketCTA}
                  />
                </>
              )}

              <div className="results-container">
                <Spacing vertical size="L">
                  {Boolean(searchIsPolling) && <LabelLoading />}
                  {hasResults || isOpenTicketList ? this.renderTripList(openTicketCTA) : null}
                  {features.NEW_FILTERS_VERSION &&
                  tripsWithoutFilters.length &&
                  hasResults &&
                  !isOpenTicketList ? (
                    <>
                      <Text weight="bold" size="L" mobileSize="M">
                        {t('search:label.other_trips_you_may_be_interested_on')}
                      </Text>
                      {this.renderTripListWithoutFilters(openTicketCTA)}
                    </>
                  ) : null}

                  {tripTerminalInfo.hasCoords && (
                    <RouteTerminalsDisplay
                      terminalOrigin={tripTerminalInfo.originTerminal}
                      cityOrigin={tripTerminalInfo.originCity}
                      terminalDestination={tripTerminalInfo.destinationTerminal}
                      cityDestination={tripTerminalInfo.destinationCity}
                      originUrl={tripTerminalInfo.originMapUrl}
                      destinationUrl={tripTerminalInfo.destinationMapUrl}
                    />
                  )}

                  <ClosedTripsSection
                    closedTrips={closedTrips}
                    selectTrip={this.selectTrip}
                    isRoundTrip={roundTrip}
                    way="return"
                    isLogged={isLogged}
                    filtersApplied={filters}
                    features={features}
                    origin={origin}
                    destination={destination}
                  />

                  <Spacing vertical size="S">
                    <div className="results-bottom-container fade-in">
                      {Boolean(!searchIsPolling && hasAvailableTrips) && (
                        <Text size="S" color="grayLight">
                          <em>{t('general:showing_all_available_trips')}</em>
                        </Text>
                      )}
                      <SwitchDoters />
                    </div>
                    <div className="results-bottom-container fade-in">
                      {features.COUPON_ON_SEARCH && <Coupon />}
                      {!isOpenTicketList && features.PROVIDERS_SELECTION_ENABLED && (
                        <ChangeProvider
                          hasOtherProviders={providers.length > 1}
                          onChangeProviderClick={this.goToReturnProviders}
                        />
                      )}
                    </div>
                  </Spacing>

                  <Visibility type="hideForLargeOnly">{TerminalsComponent}</Visibility>
                  {features.SHOW_MIN_PRICES && showDaysOpenList && (
                    <DayControls way="return" providerId={providerId} fixed />
                  )}
                  {features.SHOW_POWERED_BY && <PoweredBy />}
                </Spacing>
              </div>
            </div>
          </>
        ) : (
          <>
            <NoResults way="return" providerId={providerId} />
            <div className="returns-container">
              <div className="results-container">
                <Spacing vertical size="L">
                  <ClosedTripsSection
                    closedTrips={closedTrips}
                    selectTrip={this.selectTrip}
                    isRoundTrip={roundTrip}
                    way="return"
                    isLogged={isLogged}
                    filtersApplied={filters}
                    features={features}
                    origin={origin}
                    destination={destination}
                  />

                  {closedTrips && closedTrips.length > 0 && (
                    <Spacing vertical size="S">
                      <div className="results-bottom-container fade-in">
                        {Boolean(!searchIsPolling) && (
                          <Text size="S" color="grayLight">
                            <em>{t('general:showing_all_available_trips')}</em>
                          </Text>
                        )}
                      </div>
                      {features.SHOW_POWERED_BY && <PoweredBy />}
                    </Spacing>
                  )}
                </Spacing>
              </div>
            </div>
          </>
        )}
      </>
    );
  }
}

ProviderReturns.propTypes = propTypes;

ProviderReturns.defaultProps = {
  isOpenTicketList: false,
  setRedirectParams: () => {},
};

const mapStateToProps = (state) => ({
  features: state.whitelabelConfig.features,
});

export default connect(mapStateToProps)(ProviderReturns);
